import { ReactElement } from 'react';

import { useBillingState } from '@/modules/billing/hooks/useBillingState';
import useSegments from '@/segment/useSegments';
import { PageName } from '@/shared/types/segment';

interface PaidFeatureGateProps {
  /** Rendered for trial (unpaid) accounts. */
  paywall: ReactElement;
  /** Rendered for paid accounts. */
  children: ReactElement;
  /** The page the paywall stands in for; omit it where the gated page reports its own view. */
  pageName?: PageName;
}

/**
 * Gates a feature behind a paid plan: trial accounts see `paywall`, paid accounts see `children`.
 * Fails open — only a confirmed trial (`plan_type === 'trial'`) shows the paywall, so while billing
 * is unresolved or errors the feature renders; backend feature flags are the real entitlement check.
 *
 * A trial account never mounts the gated page, so `pageName` is what keeps the
 * paywall visible in the numbers.
 */
export const PaidFeatureGate = ({ paywall, children, pageName }: PaidFeatureGateProps) => {
  const billingState = useBillingState();
  const isTrial = billingState?.plan_type === 'trial';

  useSegments(isTrial && pageName ? pageName : null);

  return isTrial ? paywall : children;
};
